/**
 * session-recorder/ReplayScreenCanvas.tsx
 * 
 * Componente visual de renderização de tela de alta fidelidade para o Replay de Sessões.
 * Suporta três modos de fundo:
 * 1. 'live': Iframe ao vivo da aplicação renderizando a rota ativa em sandbox.
 * 2. 'mockup': Layout simulado ultra-fiel do Aluno Conectado (Sidebar, Topbar, Cards de KPIs, Gráficos e Tabelas).
 * 3. 'heatmap': Mapa de calor térmico com todos os pontos de clique da sessão do testador.
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Layers, 
  Activity, 
  MousePointer, 
  Monitor, 
  Smartphone, 
  Users, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  Radio, 
  FileText, 
  Search, 
  Bell, 
  ChevronRight, 
  Filter, 
  TrendingUp, 
  Calendar,
  Flame,
  Layout,
  Globe
} from 'lucide-react';
import type { RecordedSession, SessionEvent, MouseClickEvent } from './types';

interface ReplayScreenCanvasProps {
  session: RecordedSession;
  currentRoute: string;
  cursorPos: { x: number; y: number };
  activeClick: MouseClickEvent | null;
  recentEvents: SessionEvent[];
  currentTimeMs: number;
}

export type CanvasViewMode = 'live' | 'mockup' | 'heatmap';

export const ReplayScreenCanvas: React.FC<ReplayScreenCanvasProps> = ({
  session,
  currentRoute,
  cursorPos,
  activeClick,
  recentEvents,
  currentTimeMs
}) => {
  const [viewMode, setViewMode] = useState<CanvasViewMode>('live');
  const [iframeError, setIframeError] = useState<boolean>(false);
  const [showOverlayEvents, setShowOverlayEvents] = useState<boolean>(true);
  const heatmapCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const screenW = session.device.viewportWidth || 1280;
  const screenH = session.device.viewportHeight || 800;
  const clampedX = Math.min(100, Math.max(0, (cursorPos.x / screenW) * 100));
  const clampedY = Math.min(100, Math.max(0, (cursorPos.y / screenH) * 100));

  // Renderiza mapa de calor no modo heatmap
  useEffect(() => {
    if (viewMode !== 'heatmap' || !heatmapCanvasRef.current) return;
    const canvas = heatmapCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Filtra cliques até o tempo atual ou todos
    const clicks = session.events.filter((e): e is MouseClickEvent => e.type === 'mouse_click');

    clicks.forEach((click) => {
      const x = (click.x / screenW) * canvas.width;
      const y = (click.y / screenH) * canvas.height;
      const isPast = click.timestamp <= currentTimeMs;

      const grad = ctx.createRadialGradient(x, y, 4, x, y, isPast ? 36 : 24);
      if (isPast) {
        grad.addColorStop(0, 'rgba(239, 68, 68, 0.85)'); // Vermelho vivo
        grad.addColorStop(0.4, 'rgba(245, 158, 11, 0.6)'); // Laranja
        grad.addColorStop(0.8, 'rgba(16, 185, 129, 0.3)'); // Verde
        grad.addColorStop(1, 'rgba(16, 185, 129, 0)');
      } else {
        grad.addColorStop(0, 'rgba(59, 130, 246, 0.4)');
        grad.addColorStop(1, 'rgba(59, 130, 246, 0)');
      }

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, isPast ? 36 : 24, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [viewMode, session.events, currentTimeMs, screenW, screenH]);

  // URL do Iframe para modo Live
  const iframeSrc = `${currentRoute}${currentRoute.includes('?') ? '&' : '?'}from_cockpit=1&skip_splash=1&_replay=1`;

  return (
    <div className="ac-replayer-canvas-container">
      {/* SELETOR DE MODO DE VISUALIZAÇÃO NO TOPO DA TELA */}
      <div className="ac-replayer-view-mode-bar">
        <div className="flex items-center gap-1 bg-slate-900/90 backdrop-blur-md p-1 rounded-lg border border-slate-700/80 shadow-lg text-[0.70rem]">
          <button
            onClick={() => setViewMode('live')}
            className={`px-2.5 py-1 rounded-md font-semibold flex items-center gap-1.5 transition-all ${
              viewMode === 'live' 
                ? 'bg-blue-600 text-white shadow-sm' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
            title="Exibir página real ao vivo no Iframe"
          >
            <Globe size={12} />
            Tela Real (Live)
          </button>

          <button
            onClick={() => setViewMode('mockup')}
            className={`px-2.5 py-1 rounded-md font-semibold flex items-center gap-1.5 transition-all ${
              viewMode === 'mockup' 
                ? 'bg-blue-600 text-white shadow-sm' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
            title="Exibir layout simulado em alta fidelidade"
          >
            <Layout size={12} />
            Layout Fiel
          </button>

          <button
            onClick={() => setViewMode('heatmap')}
            className={`px-2.5 py-1 rounded-md font-semibold flex items-center gap-1.5 transition-all ${
              viewMode === 'heatmap' 
                ? 'bg-amber-500 text-slate-950 font-bold shadow-sm' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
            title="Exibir mapa de calor térmico dos cliques do usuário"
          >
            <Flame size={12} />
            Mapa de Calor ({session.totalClicks} cliques)
          </button>
        </div>

        <button
          onClick={() => setShowOverlayEvents(!showOverlayEvents)}
          className="text-[0.68rem] bg-slate-900/90 text-slate-300 border border-slate-700/80 hover:bg-slate-800 px-2.5 py-1 rounded-lg font-medium shadow-md flex items-center gap-1"
          title="Alternar lista flutuante de ações"
        >
          <Activity size={12} className={showOverlayEvents ? 'text-blue-400' : 'text-slate-500'} />
          {showOverlayEvents ? 'Ocultar Ações' : 'Ver Ações'}
        </button>
      </div>

      {/* ── CAMADA DE FUNDO 1: IFRAME LIVE REAL ── */}
      {viewMode === 'live' && (
        <div className="ac-replayer-live-layer">
          <iframe
            src={iframeSrc}
            title="Replay Live Viewport"
            className="ac-replayer-live-iframe"
            onError={() => setIframeError(true)}
          />
          {iframeError && (
            <div className="ac-replayer-iframe-error">
              <p>⚠️ Modo Iframe indisponível para esta rota.</p>
              <button onClick={() => setViewMode('mockup')}>Alternar para Layout Fiel</button>
            </div>
          )}
        </div>
      )}

      {/* ── CAMADA DE FUNDO 2: SIMULADOR DE ALTA FIDELIDADE DO ALUNO CONECTADO ── */}
      {(viewMode === 'mockup' || (viewMode === 'live' && iframeError)) && (
        <div className="ac-replayer-mockup-layer">
          {/* Topbar do Aluno Conectado */}
          <div className="ac-mockup-topbar">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center text-white font-black text-xs shadow-xs">
                AC
              </div>
              <span className="font-extrabold text-xs text-slate-100 tracking-tight">ALUNO CONECTADO</span>
              <span className="text-[0.62rem] bg-blue-500/20 text-blue-300 font-mono px-1.5 py-0.5 rounded ml-1 border border-blue-500/30">
                PRODUÇÃO
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-slate-800/80 px-2.5 py-1 rounded-md border border-slate-700 text-slate-400 text-[0.68rem] w-48">
                <Search size={11} />
                <span>Pesquisar alunos, turmas...</span>
              </div>
              <Bell size={14} className="text-slate-400" />
              <div className="flex items-center gap-1.5 text-xs text-slate-200">
                <div className="w-5 h-5 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-[0.65rem]">
                  {session.user.nome.charAt(0)}
                </div>
                <span className="text-[0.70rem] font-semibold">{session.user.nome.split(' ')[0]}</span>
              </div>
            </div>
          </div>

          <div className="ac-mockup-body">
            {/* Sidebar do Aluno Conectado */}
            <div className="ac-mockup-sidebar">
              <div className="ac-mockup-nav-group">
                <span className="ac-mockup-nav-title">GESTÃO ESCOLAR</span>
                <div className={`ac-mockup-nav-item ${currentRoute.includes('dashboard') ? 'active' : ''}`}>
                  <Monitor size={13} />
                  <span>Painel de Controle</span>
                </div>
                <div className={`ac-mockup-nav-item ${currentRoute.includes('usuario') ? 'active' : ''}`}>
                  <Users size={13} />
                  <span>Usuários & Acessos</span>
                </div>
                <div className={`ac-mockup-nav-item ${currentRoute.includes('chamada') ? 'active' : ''}`}>
                  <CheckCircle size={13} />
                  <span>Chamada & Frequência</span>
                </div>
                <div className={`ac-mockup-nav-item ${currentRoute.includes('turma') ? 'active' : ''}`}>
                  <Layers size={13} />
                  <span>Turmas & Matérias</span>
                </div>
                <div className={`ac-mockup-nav-item ${currentRoute.includes('rfid') ? 'active' : ''}`}>
                  <Radio size={13} />
                  <span>Leitores RFID UHF</span>
                </div>
              </div>

              <div className="ac-mockup-nav-group">
                <span className="ac-mockup-nav-title">COMUNICAÇÃO</span>
                <div className="ac-mockup-nav-item">
                  <Smartphone size={13} />
                  <span>App Mobile / Pais</span>
                </div>
                <div className="ac-mockup-nav-item">
                  <FileText size={13} />
                  <span>Relatórios & Dossiês</span>
                </div>
              </div>
            </div>

            {/* Conteúdo Central da Tela */}
            <div className="ac-mockup-content-area">
              {/* Cabeçalho da Rota */}
              <div className="ac-mockup-content-header">
                <div>
                  <div className="flex items-center gap-1.5 text-[0.65rem] text-slate-400 font-medium">
                    <span>Sistema</span>
                    <ChevronRight size={10} />
                    <span className="text-blue-400 font-semibold">{currentRoute}</span>
                  </div>
                  <h2 className="text-sm font-bold text-slate-100 mt-0.5">
                    {currentRoute.includes('dashboard') ? 'Visão Geral — Frequência & Operação' :
                     currentRoute.includes('usuario') ? 'Controle de Usuários e Permissões' :
                     currentRoute.includes('chamada') ? 'Registro de Presença em Tempo Real' :
                     'Módulo Oficial Aluno Conectado'}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.65rem] bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded border border-emerald-500/30 flex items-center gap-1 font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    ONLINE
                  </span>
                </div>
              </div>

              {/* Grid de Cards de Métricas (KPIs) */}
              <div className="ac-mockup-kpis-grid">
                <div className="ac-mockup-kpi-card">
                  <div className="flex items-center justify-between">
                    <span className="text-[0.65rem] text-slate-400 uppercase font-bold">Presença Hoje</span>
                    <TrendingUp size={14} className="text-emerald-400" />
                  </div>
                  <div className="text-lg font-black text-slate-100 mt-1">94.8%</div>
                  <span className="text-[0.62rem] text-emerald-400 font-semibold">+1.4% vs semana anterior</span>
                </div>

                <div className="ac-mockup-kpi-card">
                  <div className="flex items-center justify-between">
                    <span className="text-[0.65rem] text-slate-400 uppercase font-bold">Alunos Presentes</span>
                    <Users size={14} className="text-blue-400" />
                  </div>
                  <div className="text-lg font-black text-slate-100 mt-1">1.240 <span className="text-[0.65rem] font-normal text-slate-400">/ 1.308</span></div>
                  <span className="text-[0.62rem] text-slate-400">68 ausências registradas</span>
                </div>

                <div className="ac-mockup-kpi-card">
                  <div className="flex items-center justify-between">
                    <span className="text-[0.65rem] text-slate-400 uppercase font-bold">Antenas RFID</span>
                    <Radio size={14} className="text-purple-400" />
                  </div>
                  <div className="text-lg font-black text-slate-100 mt-1">4 / 4</div>
                  <span className="text-[0.62rem] text-purple-300">Portaria A e B ativas</span>
                </div>

                <div className="ac-mockup-kpi-card">
                  <div className="flex items-center justify-between">
                    <span className="text-[0.65rem] text-slate-400 uppercase font-bold">Disparos Push</span>
                    <Smartphone size={14} className="text-amber-400" />
                  </div>
                  <div className="text-lg font-black text-slate-100 mt-1">1.192</div>
                  <span className="text-[0.62rem] text-amber-300">Notificações enviadas aos pais</span>
                </div>
              </div>

              {/* Tabela de Dados e Ações */}
              <div className="ac-mockup-table-box">
                <div className="ac-mockup-table-header">
                  <span className="text-xs font-bold text-slate-200">Últimos Registros da Unidade</span>
                  <span className="text-[0.62rem] text-slate-400 font-mono">Atualizado em tempo real</span>
                </div>
                <div className="ac-mockup-table-rows">
                  {[
                    { nome: 'Ana Beatriz Souza', turma: '8º Ano A', hora: '07:14:22', status: 'Presente (RFID)' },
                    { nome: 'Carlos Eduardo Lima', turma: '9º Ano B', hora: '07:15:08', status: 'Presente (RFID)' },
                    { nome: 'Gabriel Henrique Silva', turma: '7º Ano C', hora: '07:18:40', status: 'Presente (App)' },
                    { nome: 'Julia Martins Ferreira', turma: '8º Ano A', hora: '07:22:15', status: 'Presente (RFID)' },
                  ].map((row, idx) => (
                    <div key={idx} className="ac-mockup-table-row">
                      <span className="text-[0.70rem] font-bold text-slate-200">{row.nome}</span>
                      <span className="text-[0.65rem] text-slate-400">{row.turma}</span>
                      <span className="text-[0.65rem] font-mono text-slate-400">{row.hora}</span>
                      <span className="text-[0.60rem] font-extrabold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                        {row.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CAMADA DE FUNDO 3: HEATMAP CANVAS ── */}
      {viewMode === 'heatmap' && (
        <div className="ac-replayer-heatmap-layer">
          <canvas
            ref={heatmapCanvasRef}
            width={screenW}
            height={screenH}
            className="ac-replayer-heatmap-canvas"
          />
          <div className="ac-replayer-heatmap-legend">
            <span className="font-bold text-slate-200 flex items-center gap-1">
              <Flame size={14} className="text-red-500" /> Mapa Térmico de Interações
            </span>
            <div className="flex items-center gap-2 text-[0.65rem] text-slate-400 mt-1">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" /> Pontos de Passagem</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" /> Foco do Cursor</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> Cliques Efetivos</span>
            </div>
          </div>
        </div>
      )}

      {/* ── OVERLAY DE EVENTOS RECENTES ── */}
      {showOverlayEvents && (
        <div className="ac-replayer-events-overlay">
          <div className="ac-replayer-events-title">
            <Activity size={13} /> Fluxo de Ações do Testador:
          </div>
          {recentEvents.length === 0 ? (
            <span className="text-[0.65rem] text-slate-400 italic">Aguardando ações...</span>
          ) : (
            recentEvents.map((ev, i) => (
              <div key={i} className={`ac-replayer-event-pill ac-event--${ev.type}`}>
                {ev.type === 'mouse_click' && (
                  <span>👆 Clique em <strong>{ev.targetTag}</strong> {ev.targetText ? `("${ev.targetText}")` : ''}</span>
                )}
                {ev.type === 'route_change' && (
                  <span>🔄 Navegou para <strong>{ev.path}</strong></span>
                )}
                {ev.type === 'scroll' && (
                  <span>📜 Rolagem: Y={ev.scrollY}px</span>
                )}
                {ev.type === 'error' && (
                  <span style={{ color: '#ef4444' }}>❌ Erro: {ev.message}</span>
                )}
                {ev.type === 'pause_idle' && (
                  <span>⏸️ Pausa ociosa ({Math.round((ev.idleDurationMs || 0) / 1000)}s)</span>
                )}
                {ev.type === 'resume_active' && (
                  <span>▶️ Retomou atividade</span>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── CURSOR DO MOUSE SIMULADO ── */}
      <div 
        className="ac-replayer-cursor"
        style={{
          left: `${clampedX}%`,
          top: `${clampedY}%`,
        }}
      >
        <MousePointer size={22} className="ac-replayer-cursor-icon" />
        <div className="ac-replayer-cursor-tag">
          {session.user.nome.split(' ')[0]}
        </div>
      </div>

      {/* ── EFEITO RIPPLE DE CLIQUE ── */}
      {activeClick && (
        <div 
          className="ac-replayer-click-ripple"
          style={{
            left: `${clampedX}%`,
            top: `${clampedY}%`,
          }}
        >
          <div className="ac-ripple-circle" />
          {activeClick.targetText && (
            <div className="ac-ripple-label">{activeClick.targetText}</div>
          )}
        </div>
      )}
    </div>
  );
};
